import { useState, type CSSProperties, type DragEvent, type ReactNode } from 'react';
import { Skeleton } from './Note';

/**
 * Доска CRM (кит 2.14, Board): колонки этапов с точкой и счётчиком,
 * без подложек; число колонок — переменной `--n`. Пустая колонка —
 * слово «пусто». Перетаскивание — HTML5 drag&drop: колонка, которая
 * не принимает карточку, не подсвечивается.
 */
export function Board({ columns, children }: { columns: number; children: ReactNode }) {
  return (
    <div className="board" style={{ '--n': columns } as CSSProperties}>
      {children}
    </div>
  );
}

export function BoardColumn({
  title,
  dot,
  count,
  note,
  onDrop,
  children,
}: {
  title: string;
  /** Роль этапа — цвет точки (кит 2.14) */
  dot: StageTone;
  count: number;
  /** «ставится сама» — подпись под автоматическим этапом */
  note?: string;
  /** Колонка принимает карточку: id перетаскиваемой */
  onDrop?: (id: string) => void;
  children?: ReactNode;
}) {
  const [over, setOver] = useState(false);
  const accept = (e: DragEvent) => {
    if (!onDrop) return;
    e.preventDefault();
    setOver(true);
  };
  return (
    <div>
      <div className="colh">
        <StageDot tone={dot} />
        <span className="t">{title}</span>
        <span className="c">{count}</span>
      </div>
      <div
        className={over ? 'bcol over' : 'bcol'}
        onDragOver={accept}
        onDragLeave={() => setOver(false)}
        onDrop={(e) => {
          e.preventDefault();
          setOver(false);
          const id = e.dataTransfer.getData('text/plain');
          if (id && onDrop) onDrop(id);
        }}
      >
        {children ?? <div className="colempty">пусто</div>}
      </div>
      {note ? <div className="colnote">{note}</div> : null}
    </div>
  );
}

/** Карточка доски `.dcard`: название, строки, подвал с одним сигналом и аватаром */
export function DealCard({
  id,
  title,
  meta,
  footer,
  draggable,
  onOpen,
}: {
  id: string;
  title: string;
  meta?: ReactNode;
  footer?: ReactNode;
  draggable?: boolean;
  onOpen: () => void;
}) {
  const [grab, setGrab] = useState(false);
  return (
    <div
      className={grab ? 'dcard grab' : 'dcard'}
      role="link"
      tabIndex={0}
      draggable={draggable}
      onDragStart={(e) => {
        e.dataTransfer.setData('text/plain', id);
        setGrab(true);
      }}
      onDragEnd={() => setGrab(false)}
      onClick={onOpen}
      onKeyDown={(e) => {
        if (e.key === 'Enter') onOpen();
      }}
    >
      <div className="n">{title}</div>
      {meta ? <div className="mt">{meta}</div> : null}
      {footer ? <div className="ft">{footer}</div> : null}
    </div>
  );
}

/**
 * Роль этапа в процессе — цвет точки `.sdot` (кит 2.14): идёт работа —
 * info, ждём события — warn, нейтральный факт — neutral, успех — ok.
 */
export type StageTone = 'info' | 'warn' | 'neutral' | 'ok';

export function StageDot({ tone }: { tone: StageTone }) {
  return <span className={`sdot ${tone}`} />;
}

/** Загрузка доски — колонки той же формы с карточками-скелетами */
export function BoardSkeleton({ columns }: { columns: number }) {
  return (
    <Board columns={columns}>
      {Array.from({ length: columns }, (_, i) => (
        <div key={i}>
          <div className="colh">
            <Skeleton width="50%" />
          </div>
          {[0, 1].map((k) => (
            <div className="dcard" key={k}>
              <Skeleton width="70%" />
              <div className="mt">
                <Skeleton width="45%" height={10} />
              </div>
            </div>
          ))}
        </div>
      ))}
    </Board>
  );
}
